<script setup>
import {
    Listbox as HeadlessUiListbox,
    ListboxButton as HeadlessUiListboxButton,
    ListboxLabel as HeadlessUiListboxLabel,
    ListboxOption as HeadlessUiListboxOption,
    ListboxOptions as HeadlessUiListboxOptions,
} from "@headlessui/vue";
import { computed, ref, useSlots } from "vue";
import _find from "lodash/find";
import { ChevronUpDownIcon, XMarkIcon } from "@heroicons/vue/20/solid";
import { FormHint, FormLabel, FormErrors } from "@Components/Form";
import { twMerge } from "tailwind-merge";

defineOptions({
    inheritAttrs: false,
});

const props = defineProps({
    modelValue: {},
    defaultValue: {
        default: undefined,
    },
    label: {
        type: String,
        default: undefined,
    },
    groups: {
        type: Array,
        default: [],
    },
    readonly: {
        type: Boolean,
        default: false,
    },
    itemKey: {
        type: String,
        default: "id",
    },
    itemDisplay: {
        type: String,
        default: "name",
    },
    itemIcon: {
        type: String,
        default: "icon",
    },
    showIcon: {
        type: Boolean,
        default: true,
    },
    hideGroup: {
        type: Boolean,
        default: false,
    },
    hint: {
        type: String,
    },
    help: {
        type: String,
    },
    z: {
        type: Number,
        default: 0,
    },
    required: {
        type: Boolean,
        default: false,
    },
    errors: {
        type: Array,
        default: [],
    },
});
const emit = defineEmits(["update:modelValue"]);
const slots = useSlots();

const activeItem = ref(null);

const selected = computed(() => {
    let item = undefined;
    try {
        props.groups.forEach((group) => {
            item = _find(group.items, [props.itemKey, props.modelValue]);
            if (item) {
                item.group = group.name;
                throw new Error("Item found");
            }
        });
    } catch (e) {}

    return item;
});

if (props.modelValue === undefined && props.defaultValue !== undefined) {
    emit("update:modelValue", props.defaultValue);
}

const focused = ref(false);

const buttonClasses = computed(() => {
    let classes = [
        "block w-full rounded-md border-0 py-1.5 pl-3 shadow-sm ring-1 ring-inset focus:ring-2 focus:ring-inset text-sm leading-6",
        "text-gray-900 bg-white ring-gray-300 focus:border-gray-950 focus:ring-gray-950 placeholder:text-gray-400 focus-visible:outline-none",
        props.required ? "pr-8" : "pr-14",
    ];

    switch (props.z) {
        case 3:
            classes.push(
                "dark:text-white dark:bg-gray-600 dark:ring-gray-500 dark:focus:border-gray-400 dark:focus:ring-gray-400 dark:placeholder:text-gray-300"
            );
            break;
        case 2:
            classes.push(
                "dark:text-white dark:bg-gray-700 dark:ring-gray-600 dark:focus:border-gray-500 dark:focus:ring-gray-500 dark:placeholder:text-gray-400"
            );
            break;
        case 1:
            classes.push(
                "dark:text-white dark:bg-gray-800 dark:ring-gray-700 dark:focus:border-gray-600 dark:focus:ring-gray-600 dark:placeholder:text-gray-400"
            );
            break;
        default:
            classes.push(
                "dark:text-white dark:bg-gray-900 dark:ring-gray-800 dark:focus:border-gray-700 dark:focus:ring-gray-700 dark:placeholder:text-gray-400"
            );
            break;
    }

    if (props.errors.length > 0) {
        classes.push("ring-red-500 focus:border-red-500 focus:ring-red-500");
    }

    return twMerge(classes);
});

const buttonIconClasses = computed(() => {
    let classes = ["w-5 h-5 text-gray-400 cursor-pointer hover:text-gray-500"];

    switch (props.z) {
        case 3:
            classes.push("dark:text-gray-300 hover:dark:text-gray-100");
            break;
        case 2:
            classes.push("dark:text-gray-400 hover:dark:text-gray-300");
            break;
        case 1:
            classes.push("dark:text-gray-400 hover:dark:text-gray-300");
            break;
        default:
            classes.push("dark:text-gray-400 hover:dark:text-gray-300");
            break;
    }

    return twMerge(classes);
});

const optionsClasses = computed(() => {
    let classes = ["absolute z-20 mt-1 focus:outline-none flex flex-start gap-1"];

    return twMerge(classes);
});

const optionsPartClasses = computed(() => {
    let classes = ["overflow-auto flex-none text-sm rounded-md shadow-lg max-h-60 ring-1 ring-opacity-5 focus:outline-none", "bg-white ring-black"];

    switch (props.z) {
        case 3:
            classes.push("dark:bg-gray-600 dark:ring-gray-500");
            break;
        case 2:
            classes.push("dark:bg-gray-700 dark:ring-gray-600");
            break;
        case 1:
            classes.push("dark:bg-gray-800 dark:ring-gray-700");
            break;
        default:
            classes.push("dark:bg-gray-900 dark:ring-gray-800");
            break;
    }

    return twMerge(classes);
});

const groupOptionClasses = computed(() => {
    let classes = ["px-3 py-2 text-sm bg-gray-100 text-gray-700 dark:text-gray-200"];

    switch (props.z) {
        case 3:
            classes.push("dark:bg-gray-500");
            break;
        case 2:
            classes.push("dark:bg-gray-600");
            break;
        case 1:
            classes.push("dark:bg-gray-700");
            break;
        default:
            classes.push("dark:bg-gray-800");
            break;
    }

    return twMerge(classes);
});

const activeOptionClasses = computed(() => {
    let classes = ["bg-gray-50"];

    switch (props.z) {
        case 3:
            classes.push("dark:bg-gray-500");
            break;
        case 2:
            classes.push("dark:bg-gray-600");
            break;
        case 1:
            classes.push("dark:bg-gray-700");
            break;
        default:
            classes.push("dark:bg-gray-800");
            break;
    }

    return twMerge(classes);
});
</script>

<template>
    <div>
        <HeadlessUiListbox
            as="div"
            :modelValue="modelValue"
            @update:modelValue="(value) => $emit('update:modelValue', value)"
            v-slot="{ open }"
            :disabled="readonly">
            <div :class="[label ? 'justify-between' : 'justify-end', 'flex items-center space-x-3']">
                <HeadlessUiListboxLabel :as="FormLabel" v-if="label">
                    {{ label }}
                </HeadlessUiListboxLabel>
                <slot name="hint">
                    <FormHint v-if="hint" :z="z">
                        {{ hint }}
                    </FormHint>
                </slot>
            </div>
            <div :class="[label || hint || !!slots.hint ? 'mt-2' : '', 'relative']">
                <HeadlessUiListboxButton @focus="focused = true" @blur="focused = false" :class="buttonClasses">
                    <div class="flex items-center gap-3">
                        <component v-if="showIcon && selected?.[itemIcon]" :is="selected?.[itemIcon]" class="w-5 h-5 flex-none" />
                        <div class="block text-sm leading-6 text-left text-gray-900 truncate whitespace-nowrap dark:text-white">
                            {{ selected?.name || "&nbsp;" }}
                        </div>
                    </div>
                    <div v-if="!required && selected !== undefined && !readonly" class="absolute inset-y-0 right-0 flex items-center pr-8">
                        <XMarkIcon @click.stop="$emit('update:modelValue', undefined)" :class="buttonIconClasses" aria-hidden="true" />
                    </div>
                    <div class="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                        <ChevronUpDownIcon :class="buttonIconClasses" aria-hidden="true" />
                    </div>
                </HeadlessUiListboxButton>

                <transition leave-active-class="transition duration-100 ease-in" leave-from-class="opacity-100" leave-to-class="opacity-0">
                    <HeadlessUiListboxOptions :class="optionsClasses">
                        <div :class="optionsPartClasses">
                            <template v-for="(group, index) in groups" :key="index">
                                <div :class="groupOptionClasses">{{ group.name }}</div>
                                <HeadlessUiListboxOption
                                    as="template"
                                    v-for="item in group.items"
                                    :key="item[itemKey]"
                                    :value="item[itemKey]"
                                    v-slot="{ active, selected }"
                                    :disabled="item.disabled">
                                    <li
                                        @mouseenter="activeItem = item"
                                        @mouseleave="activeItem = null"
                                        class="relative p-1 text-gray-900 cursor-default select-none group dark:text-white">
                                        <div :class="[active ? activeOptionClasses : '', 'rounded-sm py-2 px-3 flex items-center gap-3']">
                                            <component v-if="showIcon && item[itemIcon]" :is="item[itemIcon]" class="w-5 h-5 flex-none" />
                                            <div class="flex flex-wrap flex-1 justify-between items-center gap-x-3 gap-y-0.5">
                                                <div :class="[selected ? 'font-semibold' : 'font-normal', 'block truncate whitespace-nowrap']">
                                                    {{ item[itemDisplay] }}
                                                </div>
                                            </div>
                                        </div>
                                    </li>
                                </HeadlessUiListboxOption>
                            </template>
                        </div>
                        <div v-if="activeItem" :class="optionsPartClasses">
                            <slot name="details" :item="activeItem" />
                        </div>
                    </HeadlessUiListboxOptions>
                </transition>
            </div>
        </HeadlessUiListbox>
        <div v-if="help" class="mt-2 text-xs text-gray-400">{{ help }}</div>
        <FormErrors v-if="errors" class="mt-2" :errors="errors" />
    </div>
</template>
